<template>
  <div class="cp" data-pfe-menu-root @keydown.esc="onEscape">
    <div
      class="cp-trigger"
      :class="{ 'cp-trigger--open': open }"
      role="button"
      tabindex="0"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      aria-label="Procedure codes"
      data-test="pfe-code-trigger"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span
        v-for="chip in chips"
        :key="chip.value"
        class="cp-chip"
        :title="chip.text"
        data-test="pfe-code-chip"
      >
        <span class="cp-chip-text"><span class="cp-chip-code">{{ chip.code }}</span><template v-if="chip.desc"> - {{ chip.desc }}</template></span>
        <button
          type="button"
          class="cp-chip-x"
          :aria-label="'Remove ' + chip.code"
          data-test="pfe-code-chip-remove"
          @click.stop="remove(chip.value)"
        ><i class="fa fa-times" aria-hidden="true"></i></button>
      </span>
      <span v-if="chips.length === 0" class="cp-placeholder" data-test="pfe-code-placeholder">Add procedure codes</span>
      <i class="fa fa-chevron-down cp-chev" aria-hidden="true"></i>
    </div>

    <pop-menu v-if="open" :max-height="360" :min-width="320" data-test="pfe-code-menu">
      <template #header>
        <div class="cp-search">
          <i class="fa fa-search" aria-hidden="true"></i>
          <input
            ref="search"
            v-model="query"
            type="text"
            class="cp-search-input"
            placeholder="Search by code or description"
            aria-label="Search procedure codes"
            data-test="pfe-code-search"
          />
        </div>
      </template>
      <div v-if="loading && options.length === 0" class="cp-empty" data-test="pfe-code-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading procedure codes&hellip;
      </div>
      <button
        v-for="o in result.items"
        :key="o.value"
        type="button"
        class="cp-option"
        data-test="pfe-code-option"
        :data-code="o.code"
        @click="add(o.value)"
      >
        <span class="cp-option-code">{{ o.code }}</span>
        <span class="cp-option-desc">{{ o.desc }}</span>
      </button>
      <div v-if="result.truncated" class="cp-more" data-test="pfe-code-more">
        Showing the first {{ result.items.length }} of {{ result.total }} &mdash; keep typing to narrow the list.
      </div>
      <div v-if="!loading && result.items.length === 0" class="cp-empty" data-test="pfe-code-none">
        <template v-if="query.trim()">No codes match &ldquo;{{ query.trim() }}&rdquo;.</template>
        <template v-else>No more procedure codes to add.</template>
      </div>
    </pop-menu>
  </div>
</template>

<script>
import PopMenu from './PopMenu.vue';
import { chipsForCodes, filterCodeOptions, rowCodes } from '@/utils/postOp/rows';

/**
 * The code picker: chips for the row's codes, and a searchable menu to add more (design: "Group & procedure codes").
 * `value` is the row's stored `codes` — String(CodeNum || ProcCode) — and `options` the buildCodeOptions() catalog.
 * A stored code the catalog does not know stays a bare chip (never filtered away; see chipsForCodes).
 */
export default {
  name: 'PfeCodePicker',
  components: { PopMenu },
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** "+ Add row" opens the menu on the new row (design). */
    initialOpen: { type: Boolean, default: false },
  },
  data() {
    return { open: false, query: '' };
  },
  computed: {
    chips() { return chipsForCodes(this.value, this.options); },
    result() { return filterCodeOptions(this.options, this.value, this.query, 200); },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside, true);
    if (this.initialOpen) this.show();
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside, true);
  },
  methods: {
    show() {
      this.open = true;
      this.query = '';
      this.$nextTick(() => { if (this.$refs.search) this.$refs.search.focus(); });
    },
    toggle() {
      if (this.open) this.open = false;
      else this.show();
    },
    close() { this.open = false; },
    onOutside(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) this.close();
    },
    onEscape(e) {
      // the editor closes on Escape too — a menu that is open answers it first
      if (!this.open) return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    },
    add(value) {
      this.$emit('input', rowCodes({ codes: this.value }).concat([value]));
      this.query = '';
      this.$nextTick(() => { if (this.$refs.search) this.$refs.search.focus(); });
    },
    remove(value) {
      this.$emit('input', rowCodes({ codes: this.value }).filter((c) => c !== value));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.cp { position: relative; min-width: 0; }

.cp-trigger {
  @include pfe-field(42px);
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px 34px 6px 6px;
  cursor: pointer;

  &:focus-visible, &--open { @include pfe-focus-ring; }
}
.cp-chev {
  position: absolute;
  right: 12px;
  top: 14px;
  font-size: 12px;
  color: $pfe-neutral-400;
}
.cp-placeholder {
  padding: 0 6px;
  font-size: 14px;
  color: $pfe-neutral-400;
}
.cp-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  height: 28px;
  padding: 0 3px 0 10px;
  border-radius: 999px;
  background: $pp-brand-subtle;
  color: $pp-brand-deep;
  font-size: 13px;
  font-weight: 600;
}
.cp-chip-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cp-chip-code { font-family: $pp-font-mono; font-weight: 700; }
.cp-chip-x {
  flex: none;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: $pp-brand-deep;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: $pp-brand-border; }
}

.cp-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 6px;
  color: $pfe-neutral-400;
}
.cp-search-input {
  flex: 1;
  min-width: 0;
  height: 34px;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: $pp-text;

  &:focus { outline: none; }
}
.cp-option {
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 13.5px;
  text-align: left;
  color: $pp-text;
  cursor: pointer;

  &:hover, &:focus-visible { background: $pfe-neutral-50; outline: none; }
}
.cp-option-code { flex: none; font-family: $pp-font-mono; font-weight: 700; }
.cp-option-desc { min-width: 0; color: $pfe-neutral-600; }
.cp-empty, .cp-more {
  padding: 14px 10px;
  font-size: 13.5px;
  color: $pfe-neutral-500;
}
.cp-more { padding-top: 4px; font-size: 12.5px; }
</style>
